import { useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import { Row, TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { IconButton, PopoverMenuItem, PopoverMenu, usePopoverMenu } from '../../';
import { MoreIcon } from '../../../icon';
import { TableData } from '../table-data-type';
import { BulkAction, TableBulkActions } from '../table-types';

const bulkActionsWrapperStyle = css`
  display: flex;
  align-items: center;
`;

const bulkActionsWrapperMobileStyle = css`
  margin-left: ${theme.spacing(1)};
`;

const bulkActionsWrapperDesktopStyle = css`
  margin-left: ${theme.spacing(2)};
`;

const bulkActionIconBtnStyle = css`
  margin: 0 5px;
`;

const bulkActionMobileIconBtnStyle = css`
  margin-left: 8px;
`;

type BulkActionsProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  selectedRows: Row<T>[];
  bulkActions?: TableBulkActions<T>[];
};

export const BulkActions = <T extends TableData>({ tableInstance, selectedRows, bulkActions }: BulkActionsProps<T>) => {
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu<
    HTMLButtonElement | HTMLAnchorElement,
    false,
    HTMLMenuElement,
    HTMLAnchorElement | HTMLButtonElement
  >({
    interactionOptions: {
      listNavigation: {
        openOnArrowKeyDown: false,
      },
    },
    placement: 'right-start',
  });
  const { isMobile } = tableInstance;
  const selectedRowsData = useMemo(
    () => selectedRows.map((selectedRow) => selectedRow.original),
    [selectedRows]
  ) as T[];

  type TODO = any;

  const renderActions = () => {
    return bulkActions?.map(({ onClick, Icon, ...rest }: TableBulkActions<T>, id) => {
      const { hide, ...dynamicProps } = Object.entries(rest).reduce(
        (acc, [key, value]) => ({
          ...acc,
          ...(key !== 'onClick'
            ? { [key]: typeof value !== 'function' ? value : (value as TODO)(selectedRowsData) }
            : {}),
        }),
        {} as Omit<BulkAction<T>, 'onClick' | 'Icon'>
      );

      const itemProps = getItemProps({
        index: id,
        onClick: (e) => {
          e.stopPropagation();
          onClick?.(selectedRows, selectedRowsData);
          close();
        },
      });

      if (isMobile && !hide) {
        return (
          <PopoverMenuItem
            key={dynamicProps.label + id}
            Icon={Icon}
            {...itemProps}
            {...(dynamicProps as any)} //TODO: Fix this assertion
          >
            {dynamicProps.label}
          </PopoverMenuItem>
        );
      }

      return !hide ? (
        <IconButton
          key={id}
          className={bulkActionIconBtnStyle}
          color='light'
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(selectedRows, selectedRowsData);
          }}
          {...dynamicProps}
          label={dynamicProps.label}
          showLabelOnHover
        >
          <Icon />
        </IconButton>
      ) : null;
    });
  };

  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();

  if (bulkActions?.length === 0) return null;

  return (
    <div
      className={cx(bulkActionsWrapperStyle, isMobile ? bulkActionsWrapperMobileStyle : bulkActionsWrapperDesktopStyle)}
    >
      {isMobile ? (
        <>
          <IconButton className={bulkActionMobileIconBtnStyle} color='light' label='Action menu' {...triggerProps}>
            <MoreIcon size={20} />
          </IconButton>
          <PopoverMenu {...menuProps} title='' css={{ maxWidth: 320 }}>
            {renderActions()}
          </PopoverMenu>
        </>
      ) : (
        renderActions()
      )}
    </div>
  );
};
